import type { AppLocale } from '../i18n';
import type {
  LessonDetailDto,
  LessonExerciseSummaryDto,
  LessonQuizDto,
} from '../services/contentService';
import { pickLocalizedTitle } from './localizedContent';
import { isDesignedForNonFarsiUsers } from './nonFarsiContentHint';
import type { JourneyStepStatus, LessonJourneyStep } from '../components/learning/LessonJourneyTimeline';
import { isInlineLessonExercise } from './lessonExerciseInline';

export function resolveLessonQuizzes(lesson: LessonDetailDto): LessonQuizDto[] {
  if (lesson.lessonQuizzes?.length) {
    return [...lesson.lessonQuizzes].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }
  return lesson.lessonQuiz ? [lesson.lessonQuiz] : [];
}

function mandatoryExercises(lesson: LessonDetailDto): LessonExerciseSummaryDto[] {
  return (lesson.exercises ?? [])
    .filter((e) => e.mandatoryForPassingLesson)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

function mandatoryInlineExercises(lesson: LessonDetailDto): LessonExerciseSummaryDto[] {
  return mandatoryExercises(lesson).filter(isInlineLessonExercise);
}

/** True when there are no mandatory inline exercises, or all are completed. */
export function areMandatoryInlineExercisesComplete(lesson: LessonDetailDto): boolean {
  const mandInline = mandatoryInlineExercises(lesson);
  return (
    mandInline.length === 0 ||
    mandInline.every((e) => e.userHasCompletedExercise === true)
  );
}

function mandatoryEndOfLessonExercises(lesson: LessonDetailDto): LessonExerciseSummaryDto[] {
  return mandatoryExercises(lesson).filter((e) => !isInlineLessonExercise(e));
}

function mandatoryQuizzes(lesson: LessonDetailDto): LessonQuizDto[] {
  return resolveLessonQuizzes(lesson).filter((q) => q.mandatoryForPassingLesson);
}

/** Non-sequential steps: complete vs still to do (no “current” except final pass step). */
function flexibleStepStatus(done: boolean): JourneyStepStatus {
  return done ? 'complete' : 'upcoming';
}

function finalPassStepStatus(passed: boolean, prerequisitesMet: boolean): JourneyStepStatus {
  if (passed) return 'complete';
  if (prerequisitesMet) return 'current';
  return 'upcoming';
}

export type JourneyTranslate = (key: string, options?: Record<string, unknown>) => string;

function pickJourneyItemTitle(
  item: {
    title?: string | null;
    titleFa?: string | null;
    isDesignedForNonFarsiUsers?: boolean;
  },
  locale: AppLocale
): string {
  const contentLocale = isDesignedForNonFarsiUsers(item) ? 'en' : locale;
  return pickLocalizedTitle(item, contentLocale);
}

function buildMandatoryExerciseStep(
  ex: LessonExerciseSummaryDto,
  inline: boolean,
  options: { onOpenExercise?: (exerciseId: number) => void; jt?: JourneyTranslate; locale?: AppLocale }
): LessonJourneyStep {
  const done = ex.userHasCompletedExercise === true;
  const jt = options.jt;
  const locale = options.locale ?? 'en';
  return {
    id: inline ? `inline-exercise-${ex.id}` : `exercise-${ex.id}`,
    label: pickJourneyItemTitle(ex, locale),
    hint: jt?.('journey.answerCorrectly') ?? 'Answer every question correctly',
    status: flexibleStepStatus(done),
    actionLabel: done
      ? (jt?.('exercise.practiceAgain') ?? 'Practice again')
      : (jt?.('journey.openExercise') ?? 'Open exercise'),
    onAction:
      () => options.onOpenExercise?.(ex.id)
  };
}

export type LessonJourneyPassStepOptions = {
  label: string;
  hintWhenReady: string;
  isPassed: boolean;
  /** Show passed or not, without a mark or unmark button. */
  automatic?: boolean;
};

export function buildLessonJourneySteps(
  lesson: LessonDetailDto,
  options: {
    onMarkReviewed?: () => void;
    onUnmarkReviewed?: () => void;
    onOpenExercise?: (exerciseId: number) => void;
    onOpenQuiz?: (quizId: number) => void;
    onMarkPassed?: () => void;
    onUnmarkPassed?: () => void;
    markingReview?: boolean;
    markingPassed?: boolean;
    /** Override the final “mark passed” step (e.g. tag-scoped completion). */
    passStep?: LessonJourneyPassStepOptions;
    /** i18n helper — keys under learning:journey.* */
    jt?: JourneyTranslate;
    /** UI locale for bilingual exercise/quiz titles. */
    locale?: AppLocale;
  }
): LessonJourneyStep[] {
  const jt = options.jt;
  const locale = options.locale ?? 'en';
  const reviewed = lesson.isLessonReviewed === true;
  const mandInline = mandatoryInlineExercises(lesson);
  const mandEndEx = mandatoryEndOfLessonExercises(lesson);
  const mandQz = mandatoryQuizzes(lesson);
  const mandEx = mandatoryExercises(lesson);

  const inlinePrerequisitesMet = areMandatoryInlineExercisesComplete(lesson);

  const steps: LessonJourneyStep[] = [];

  steps.push({
    id: 'review',
    label: jt?.('journey.reviewLesson') ?? 'Review the lesson',
    hint: inlinePrerequisitesMet
      ? (jt?.('journey.readAllPages') ?? 'Read through all lesson pages')
      : (jt?.('journey.completeExercisesBeforeReview') ?? 'Complete the exercises below before you can mark reviewed'),
    status: reviewed
      ? 'complete'
      : inlinePrerequisitesMet
        ? 'current'
        : 'upcoming',
    actionLabel: reviewed
      ? (jt?.('journey.markNotReviewed') ?? 'Mark as not reviewed')
      : (jt?.('journey.markReviewed') ?? 'Mark as reviewed'),
    onAction: reviewed ? options.onUnmarkReviewed : options.onMarkReviewed,
    actionDisabled:
      options.markingReview || options.markingPassed || (!reviewed && !inlinePrerequisitesMet),
    actionLoading: options.markingReview,
    children:
      mandInline.length > 0
        ? mandInline.map((ex) => buildMandatoryExerciseStep(ex, true, options))
        : undefined
  });

  for (const ex of mandEndEx) {
    steps.push(buildMandatoryExerciseStep(ex, false, options));
  }

  for (const qz of mandQz) {
    const done = qz.userHasPassedQuiz === true;
    steps.push({
      id: `quiz-${qz.id}`,
      label: pickJourneyItemTitle(qz, locale),
      hint: jt?.('journey.passWithScore', { score: qz.passingScore }) ?? `Pass with at least ${qz.passingScore}%`,
      status: flexibleStepStatus(done),
      actionLabel: done ? undefined : (jt?.('journey.takeQuiz') ?? 'Take quiz'),
      onAction: done ? undefined : () => options.onOpenQuiz?.(qz.id)
    });
  }

  const prerequisitesMet = areLessonJourneyPassPrerequisitesMet(lesson);

  const passCfg = options.passStep;
  const rawPassed = passCfg?.isPassed ?? lesson.isCompleted === true;
  /** Tag-scoped pass uses passStep.isPassed; structured lessons require prerequisites + completion. */
  const passed = passCfg != null ? passCfg.isPassed === true : rawPassed && prerequisitesMet;

  const passPrerequisitesMet =
    passCfg != null ? passCfg.isPassed === true || prerequisitesMet : prerequisitesMet;

  const passLabel = passCfg?.label ?? (jt?.('journey.markLessonPassed') ?? 'Mark lesson as passed');
  const passHintReady = passCfg?.hintWhenReady ?? (jt?.('journey.confirmFinished') ?? 'Confirm you finished this lesson');

  steps.push({
    id: 'mark-passed',
    label: passLabel,
    hint: passPrerequisitesMet
      ? passHintReady
      : (jt?.('journey.completeStepsFirst') ?? 'Complete all steps above before marking passed'),
    status: finalPassStepStatus(passed, passPrerequisitesMet),
    ...(passCfg?.automatic
      ? {}
      : {
          actionLabel: passed
            ? (jt?.('journey.unmarkPassed') ?? 'Unmark passed')
            : (jt?.('journey.markAsPassed') ?? 'Mark as passed'),
          onAction: passed ? options.onUnmarkPassed : options.onMarkPassed,
        }),
    actionDisabled:
      options.markingReview ||
      (passed ? Boolean(options.markingPassed) : !passPrerequisitesMet || Boolean(options.markingPassed)),
    actionLoading: options.markingPassed
  });

  return steps;
}

export type LessonJourneyExpandState = 'collapsed' | 'full';

export function lessonJourneyExpandStorageKey(lessonId: number): string {
  return `lesson-journey-expand:${lessonId}`;
}

export function readPersistedLessonJourneyExpand(lessonId: number): LessonJourneyExpandState | null {
  try {
    const raw = sessionStorage.getItem(lessonJourneyExpandStorageKey(lessonId));
    if (raw === 'collapsed' || raw === 'full') return raw;
    // Migrate legacy semi-open state to fully open.
    if (raw === 'partial') return 'full';
  } catch {
    /* ignore quota / private mode */
  }
  return null;
}

export function writePersistedLessonJourneyExpand(
  lessonId: number,
  state: LessonJourneyExpandState
): void {
  try {
    sessionStorage.setItem(lessonJourneyExpandStorageKey(lessonId), state);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Stable key so the journey timeline re-renders when progress changes. */
export function lessonJourneyTimelineKey(lesson: LessonDetailDto, tagPassed?: boolean): string {
  const ex = (lesson.exercises ?? [])
    .map((e) => `${e.id}:${e.userHasCompletedExercise ? 1 : 0}`)
    .join(',');
  const qz = resolveLessonQuizzes(lesson)
    .map((q) => `${q.id}:${q.userHasPassedQuiz ? 1 : 0}`)
    .join(',');
  const pass =
    tagPassed !== undefined ? (tagPassed ? 1 : 0) : lesson.isCompleted === true ? 1 : 0;
  return `${lesson.id}-r${lesson.isLessonReviewed ? 1 : 0}-p${pass}-e${ex}-q${qz}`;
}

/** True when review + all mandatory exercises and quizzes are complete. */
export function areLessonJourneyPassPrerequisitesMet(lesson: LessonDetailDto): boolean {
  const reviewed = lesson.isLessonReviewed === true;
  const mandEx = mandatoryExercises(lesson);
  const mandQz = mandatoryQuizzes(lesson);
  return (
    reviewed &&
    mandEx.every((e) => e.userHasCompletedExercise) &&
    mandQz.every((q) => q.userHasPassedQuiz)
  );
}

export function getLessonJourneyBlockers(lesson: LessonDetailDto): string[] {
  const blockers: string[] = [];
  for (const ex of mandatoryInlineExercises(lesson)) {
    if (!ex.userHasCompletedExercise) {
      blockers.push(`Complete exercise: ${ex.title}`);
    }
  }
  if (!lesson.isLessonReviewed) blockers.push('Review the lesson content');
  for (const ex of mandatoryEndOfLessonExercises(lesson)) {
    if (!ex.userHasCompletedExercise) {
      blockers.push(`Complete exercise: ${ex.title}`);
    }
  }
  for (const qz of mandatoryQuizzes(lesson)) {
    if (!qz.userHasPassedQuiz) blockers.push(`Pass quiz: ${qz.title}`);
  }
  return blockers;
}
